import React from "react"
import styled from "styled-components"

import { H2, H4 } from "components/text"
import { MainContainer } from "components/main-container"
import { PrimaryButton } from "components/buttons"

import { SvgBackgroundBox } from "components/marketing/svg-background-box"
import { ProgressiveImageSrcSet } from "shared/image-content/image-srcset"
import { LoadingWrapper } from "components/loading-wrapper"
import { Flex, Box, FlexColumn } from "components/box"
import { H3, Paragraph } from "components/text"
import Grid, { Col } from "components/grid"

import { SectionsPanel } from "./components/sections-panel"
import { PostSummaryList } from "./components/post-summary-list"

import { getFormatDiscountPrice } from "shared/products/helpers"
import { useRootQuery } from "graphql/smart-queries/user"
import { baseRoutes } from "routes"
import theme from "theme"
import LearnPromo from "assets/backgrounds/learn-promo.jpg"

import LearnScrollToTop from "components/scroll-to-top"

const LearnContainer = styled(Box)`
  background: ${props => props.theme.colors.white};
  -webkit-overflow-scrolling: touch;
`

const LearnScrollToTopContainer = styled(Flex).attrs({
  justifyContent: ["center", null, "flex-end"],
  px: [0, null, "2.063rem"],
  py: ["3.063rem", null, "2.313rem"]
})``

const HeroBlob = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="1440" height="477" viewBox="0 0 1440 477">
    <path
      fill={theme.colors.paleAqua}
      d="M17.523 213.333C-193.917-.158-292.11-398.11-125.699-598.208c166.87-200.13 613.981-171.42 894.618-113.535 281.095 57.852 396.595 144.284 600.25 325.406 204.15 181.618 335.368 444.03 265.714 599.67-69.16 156.136-282.235 346.091-710.248 223.483-428.013-122.608-695.213-10.023-907.112-223.483z"
    />
  </svg>
)

const PromoBlobMobile = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="375" height="323" viewBox="0 0 375 323">
    <path
      fill={theme.colors.iceBlue}
      d="M375.627 15.137C449.32 53.225 501.05 175.03 467.461 238.99c-33.717 64.003-152.879 70.203-235.737 77.186-82.985 7.026-129.874 15.054-199.228-15.378-69.526-30.52-161.597-99.324-152.305-144.439 9.12-45.202 119.823-66.495 222.893-100.066 103.07-33.572 198.724-79.2 272.543-41.156z"
    />
  </svg>
)

const PromoBlob = () => (
  <svg width="1440px" height="429px" viewBox="0 0 1440 429" xmlns="http://www.w3.org/2000/svg">
    <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
      <path
        d="M1538.0018,276.170375 C1647.11083,390.589912 1664.74203,642.312894 1564.57636,736.775535 C1464.15517,831.238177 1245.68159,768.440477 1092.11127,729.591053 C938.28542,690.741629 848.851789,676.106572 742.809054,578.184735 C636.510795,479.996807 513.347908,298.788191 558.064724,223.218078 C602.526015,147.381873 815.122533,177.716355 1021.58646,181.17555 C1228.05039,184.634745 1428.63724,161.750838 1538.0018,276.170375 Z"
        fill={theme.colors.iceBlue}
        fillRule="nonzero"
        transform="translate(1089.638668, 479.291569) rotate(-11.000000) translate(-1089.638668, -479.291569) "
      ></path>
      <path
        d="M512.959465,-282.681803 C590.203011,-168.262266 602.68499,83.4607156 531.772881,177.923357 C460.679875,272.385998 306.011884,209.588298 197.292044,170.738874 C88.3913068,131.88945 25.0769245,117.254393 -49.9958431,19.3325563 C-125.249509,-78.8553721 -212.442458,-260.063988 -180.785267,-335.634101 C-149.308974,-411.470306 1.19835748,-381.135824 147.364132,-377.676629 C293.529906,-374.217433 435.53502,-397.101341 512.959465,-282.681803 Z"
        fill={theme.colors.iceBlue}
        fillRule="nonzero"
        transform="translate(195.541573, -79.560609) rotate(-11.000000) translate(-195.541573, 79.560609) "
      ></path>
    </g>
  </svg>
)

export const LearnLanding: React.FC = () => {
  const { data, loading, error } = useRootQuery()
  const discount =
    data?.currentOffer?.discountPercentage &&
    getFormatDiscountPrice({ discountPercentage: data.currentOffer.discountPercentage, withPercent: true })

  return (
    <MainContainer
      mainContent={
        <LearnContainer>
          <SectionsPanel />

          <SvgBackgroundBox svg={HeroBlob} backgroundSize="cover" backgroundPosition="bottom">
            <H2 textAlign="center" pt={[7, null, 8]} pb={[7, null, 9]}>
              The Cove Blog
            </H2>
          </SvgBackgroundBox>

          <PostSummaryList />

          <Grid px={[0, 0, 0, 0]} bg="paleAqua">
            <Col cols={[4, 4, 6, 6]}>
              <ProgressiveImageSrcSet src={LearnPromo} small={LearnPromo} />
            </Col>
            <Col cols={[4, 4, 5, 5]} push={[0, 0, 0.5, 0.5]} px={[5, 6, 0, 0]}>
              <Box px={[5, 6, 0, 0]} pt={[5, null, 6]} pb={[7, null, 0]}>
                <FlexColumn alignItems={["center", null, "flex-start"]}>
                  <H3 fontSize={["2rem", null, "2.5rem"]} mb={[4, null, 5]}>
                    We offer a variety of products that can help you prevent your migraines
                  </H3>
                  <Paragraph fontSize={["1rem", null, "1.5rem"]} mb={[5, null, 6]}>
                    Choose an acute, preventative, anti-nausea, or dietary supplement that works for you.
                  </Paragraph>
                </FlexColumn>
                <PrimaryButton to={baseRoutes.ourOffering()} width={["100%", null, "60%"]}>
                  Learn more
                </PrimaryButton>
              </Box>
            </Col>
          </Grid>

          <LoadingWrapper loading={loading} error={error}>
            <SvgBackgroundBox svg={[PromoBlobMobile, PromoBlobMobile, PromoBlob, PromoBlob]} pt={[7, null, 8]}>
              <Grid rowProps={{ justifyContent: "center", textAlign: "center" }}>
                <Col cols={[4, 4, 6, 6]}>
                  <H4 fontSize={["1.625rem", null, "2rem"]} mb={[5, null, 6]}>
                    Start your consultation now to get up to {discount} off your first month of Cove
                  </H4>
                  <PrimaryButton to={baseRoutes.choosePlan()} width={["100%", null, "60%"]}>
                    Get relief now
                  </PrimaryButton>
                </Col>
              </Grid>

              <LearnScrollToTopContainer>
                <LearnScrollToTop />
              </LearnScrollToTopContainer>
            </SvgBackgroundBox>
          </LoadingWrapper>
        </LearnContainer>
      }
    />
  )
}
